/**
 * Bar chart with hover: the bar under the pointer lights up and a small
 * tooltip names the bucket and its numbers. One atom for every bar chart
 * on the Agents screens (table sparklines, the per-agent trend) so they
 * all behave the same on mouse-over and on touch (tap = hover).
 *
 * Pure presentation: caller supplies the buckets and the tooltip text.
 */

import { useState } from 'react';

import { useC } from '../../lib/theme.js';

const MONO = "'JetBrains Mono', monospace";

export interface HoverBar {
  /** Primary value (the faint bar). */
  readonly value: number;
  /** Optional emphasised value drawn on top of the primary (solid bar). */
  readonly solid?: number | undefined;
  /** Tooltip lines, first line is the bucket label. */
  readonly tooltip: readonly string[];
}

export interface HoverBarsProps {
  readonly bars: readonly HoverBar[];
  readonly width: number;
  readonly height: number;
  /** Drawn axis labels under the first and last bar. */
  readonly axis?: { readonly first: string; readonly last: string } | undefined;
  /** Stretch to container width (viewBox scaling). Default: fixed pixel size. */
  readonly fluid?: boolean | undefined;
  readonly 'aria-label'?: string | undefined;
}

export function HoverBars({ bars, width: W, height: H, axis, fluid = false, 'aria-label': ariaLabel }: HoverBarsProps): JSX.Element {
  const C = useC();
  const [hover, setHover] = useState<number | null>(null);
  const axisH = axis !== undefined ? 14 : 0;
  const plotH = H - axisH;
  const pad = axis !== undefined ? 4 : 0;
  const n = Math.max(1, bars.length);
  const bw = (W - pad * 2) / n;
  const max = Math.max(1, ...bars.map((b) => Math.max(b.value, b.solid ?? 0)));
  const hovered = hover !== null ? bars[hover] ?? null : null;
  const tipRight = hover !== null && hover > n / 2;

  return (
    <div style={{ position: 'relative', width: fluid ? '100%' : W, userSelect: 'none' }} data-testid="hover-bars">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width={fluid ? '100%' : W}
        height={H}
        preserveAspectRatio={fluid ? 'none' : 'xMidYMid meet'}
        role={ariaLabel !== undefined ? 'img' : undefined}
        aria-label={ariaLabel}
        aria-hidden={ariaLabel === undefined ? 'true' : undefined}
        style={{ display: 'block', cursor: 'crosshair', overflow: 'visible' }}
        onMouseLeave={() => setHover(null)}
      >
        {axis !== undefined ? <line x1={pad} x2={W - pad} y1={plotH} y2={plotH} stroke={C.border} strokeWidth={1} /> : null}
        {bars.map((b, i) => {
          const x = pad + i * bw;
          const vh = Math.max(b.value > 0 ? 1 : 0, Math.round((plotH - 2) * (b.value / max)));
          const sh = b.solid !== undefined && b.solid > 0 ? Math.max(1, Math.round((plotH - 2) * (b.solid / max))) : 0;
          const on = hover === i;
          return (
            <g key={i} onMouseEnter={() => setHover(i)} onTouchStart={() => setHover(i)}>
              <rect x={x} y={0} width={bw} height={plotH} fill={on ? C.surface2 : 'transparent'} />
              <rect x={x + 0.5} y={plotH - vh} width={Math.max(1, bw - 1)} height={vh} fill={C.limeFg} opacity={on ? 0.75 : 0.35} />
              {sh > 0 ? <rect x={x + 0.5} y={plotH - sh} width={Math.max(1, bw - 1)} height={sh} fill={C.limeFg} /> : null}
            </g>
          );
        })}
        {axis !== undefined ? (
          <>
            <text x={pad} y={H - 3} fontFamily={MONO} fontSize={9} fill={C.muted}>{axis.first}</text>
            <text x={W - pad} y={H - 3} fontFamily={MONO} fontSize={9} fill={C.muted} textAnchor="end">{axis.last}</text>
          </>
        ) : null}
      </svg>
      {hovered !== null ? (
        <div
          role="tooltip"
          style={{
            position: 'absolute',
            top: -4,
            ...(tipRight ? { right: `${100 - ((pad + (hover! + 0.5) * bw) / W) * 100}%` } : { left: `${((pad + (hover! + 0.5) * bw) / W) * 100}%` }),
            transform: 'translateY(-100%)',
            background: C.surface2,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            padding: '5px 8px',
            fontFamily: MONO,
            fontSize: 10,
            lineHeight: 1.5,
            color: C.text,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 20,
            boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
          }}
        >
          {hovered.tooltip.map((t, i) => (
            <div key={i} style={{ color: i === 0 ? C.muted : C.text, letterSpacing: i === 0 ? 1 : 0, textTransform: i === 0 ? 'uppercase' : 'none', fontSize: i === 0 ? 9 : 10 }}>{t}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
